import React, { FC, useEffect, useState } from 'react';
import { useApplicationContext } from '../../../../core/hooks/useApplicationContext';
import { AmountType, SelectedAmountStatuses } from '../../../../types';
import TextInput from '../../Components/TextInput';
import Button from '../../Components/Button';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { classes } from './CustomTipInput.st.css';
import { CustomTipInputDatahooks } from '../../datahooks';
import { getCustomPreset, getSelectedPreset } from '../../../../utils';

const CustomTipInput: FC = () => {
  const {
    presetInfo,
    checkoutInfo,
    selectedAmount,
    actions: { selectTipAmount },
  } = useApplicationContext();
  const { t } = useTranslation();

  const [inputValue, setInputValue] = useState<string>(
    selectedAmount?.amount || '0',
  );
  const [buttonState, setButtonState] = useState<SelectedAmountStatuses>(
    selectedAmount?.status || SelectedAmountStatuses.IDLE,
  );
  const selectedPreset =
    getSelectedPreset(presetInfo?.presets) ||
    getCustomPreset(presetInfo?.presets);

  useEffect(() => {
    if (selectedAmount?.status) {
      setButtonState(selectedAmount?.status);
    }
  }, [selectedAmount?.status]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const textInput = e.target.value;

    if (selectedAmount?.amount === textInput) {
      setButtonState(SelectedAmountStatuses.SUCCESS);
    } else {
      setButtonState(SelectedAmountStatuses.IDLE);
    }

    setInputValue(textInput);
  };

  const currency = checkoutInfo?.checkout?.currency || 'USD';

  const MAX_TIP_AMOUNT = 99999999999999;
  return (
    <div className={classes.root} data-hook={CustomTipInputDatahooks.MAIN}>
      <div className={classes.textInputWrapper}>
        <TextInput
          data-hook={CustomTipInputDatahooks.TEXT_INPUT}
          currency={currency}
          value={inputValue}
          validateRegx={/^(0|[1-9]\d*)(\.\d{0,2})?$/}
          type="number"
          inputMode="decimal"
          onChange={handleInputChange}
          min={0}
          step={0.5}
          max={MAX_TIP_AMOUNT}
          onFocus={(e) => e.target.select()}
          aria-label={t('app.wix-tips.widget.custom-amount-input.aria-label', {
            currency,
          })}
        />
      </div>
      <div className={classes.buttonWrapper}>
        <Button
          aria-busy={buttonState === SelectedAmountStatuses.IN_PROGRESS}
          data-hook={CustomTipInputDatahooks.BUTTON}
          className={classes.button}
          disabled={inputValue === '' || Number(inputValue) > MAX_TIP_AMOUNT}
          idleContent={t('app.wix-tips.widget.button.label')}
          state={buttonState}
          onNotificationEnd={() => setButtonState(SelectedAmountStatuses.IDLE)}
          onClick={() => {
            if (!selectedPreset) {
              return;
            }
            selectTipAmount({
              presetId: selectedPreset.id,
              selectedAmount: {
                isCustom: true,
                amount: inputValue,
                type: AmountType.Fixed!,
              },
            });
          }}
        />
      </div>
    </div>
  );
};

export default CustomTipInput;
